import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import posthog from "posthog-js";
import { appConfig } from "../config/appConfig";
import { useAuth } from "./AuthContext";
import { useUserStore } from "../stores/userStore";
import { useApiStore } from "../stores/apiStore";

const PostHogContext = createContext();

export const usePostHog = () => {
  const context = useContext(PostHogContext);
  if (!context) {
    throw new Error("usePostHog must be used within a PostHogProvider");
  }
  return context;
};

export const PostHogProvider = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const { isAuthenticated } = useAuth();
  const user = useUserStore((state) => state.user);
  const projectCode = useApiStore((state) => state.project);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }

    // Only initialize if we have an API key
    if (!appConfig.posthog_api_key) {
      console.warn("PostHog API key not found. Analytics will be disabled.");
      setIsInitialized(true);
      hasInitializedRef.current = true;
      return;
    }

    // Initialize PostHog
    posthog.init(appConfig.posthog_api_key, {
      api_host: "https://us.i.posthog.com",
      loaded: () => {
        setIsInitialized(true);
      },
      // Enable session recording and autocapture
      autocapture: true,
      capture_pageview: false, // We'll handle pageviews manually
      capture_pageleave: true,
      // Enable rage clicks detection
      rageclick: true,
      // Disable in development if needed
      disable_session_recording: process.env.NODE_ENV === "development",
    });
    hasInitializedRef.current = true;

    return () => {
      // Cleanup on unmount
      if (hasInitializedRef.current) {
        posthog.reset();
      }
    };
  }, []);

  // Identify user when authenticated
  useEffect(() => {
    if (isInitialized && isAuthenticated && user) {
      posthog.identify(String(user.id), {
        email: user.email,
        name: user.name || `${user.lastname || ""}`.trim(),
        user_level: user.level,
        created_at: user.created_at,
        ...(projectCode && { project_code: projectCode }),
      });

      if (!projectCode) return;

      try {
        posthog?.setPersonPropertiesForFlags?.({
          project_code: projectCode,
        });
      } catch {
        console.warn("PostHog setPersonPropertiesForFlags error:");
      }
    } else if (isInitialized && !isAuthenticated) {
      // Reset when user logs out
      posthog.reset();
    }
  }, [isInitialized, isAuthenticated, user, projectCode]);

  // Memoize track function to prevent unnecessary re-renders in consumers
  const track = useCallback(
    (eventName, properties = {}) => {
      if (isInitialized) {
        try {
          posthog.capture(eventName, properties);
        } catch {
          console.warn("PostHog track error:");
        }
      }
    },
    [isInitialized],
  );

  // Memoize identify function
  const identify = useCallback(
    (distinctId, properties = {}) => {
      if (isInitialized) {
        try {
          posthog.identify(distinctId, properties);
        } catch {
          console.warn("PostHog identify error:");
        }
      }
    },
    [isInitialized],
  );

  // Memoize reset function
  const reset = useCallback(() => {
    if (isInitialized) {
      try {
        posthog.reset();
      } catch {
        console.warn("PostHog reset error:");
      }
    }
  }, [isInitialized]);

  // Memoize the entire context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      posthog,
      isInitialized,
      track,
      identify,
      reset,
    }),
    [isInitialized, track, identify, reset],
  );

  return (
    <PostHogContext.Provider value={value}>{children}</PostHogContext.Provider>
  );
};

export default PostHogContext;
